/* ===================================================================
   Post-auth landing for the MAIN login (2026-06-22, Hopper).

   CHANGE (Steve, 2026-06-22): the main login is now the CLIENT experience.
   It must NEVER auto-bounce an admin to /agency -- otherwise Steve (an
   admin) can't sign in as a client (e.g. demo@) to see the tenant
   dashboard, and there was no way out of /agency.

   So the bare origin "/" and the main login always land on the CLIENT
   dashboard for the signed-in account's own tenant. The operator console
   (/agency) is reached ONLY via the separate admin door (/operator), which
   navigates there after login.

   Admin authorization is unchanged and still enforced SERVER-SIDE: the
   /agency route + every /admin/* API call go through the backend
   @require_admin gate (admin_users table). This file only changes the
   default CLIENT landing -- it grants no access.

   AppShell still shows an "Operator console" link in the sidebar foot for
   accounts whose getAdminMe() returns is_admin === true, so an operator who
   logged in at the main door can still jump to /agency in one click without
   re-logging-in.
   =================================================================== */
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  useMe,
  useCustomers,
  useReports,
  useSubscriptionAccess,
  useEntryModel,
} from '../hooks';

/* ONE guaranteed activation path (Phase 3, 2026-07-07).

   A brand-new tenant that has done NOTHING yet -- no Google place linked, no
   customers, no reviews -- is routed to the UNIFIED CHECKLIST HOME at
   /onboarding (the single derived-completion setup surface) instead of a bare
   dashboard. Everyone else lands on /dashboard, which itself OVERLAYS the same
   checklist (embedded variant) while the tenant still has zero reviews (§9). So
   a zero-data owner always meets the checklist home, never a blank dashboard:

       brand-new  -> /onboarding checklist home
       zero-data  -> /dashboard -> embedded checklist overlay (0 reviews)
       has data   -> /dashboard (real)

   We wait for the signals to resolve before deciding so a slow load never
   flashes the checklist at a tenant that already has data. */
export function PostAuthLanding() {
  const me = useMe();
  const customers = useCustomers({ limit: 1 });
  const reports = useReports();

  /* ADMIN-ONLY-ACCOUNT LANDING (2026-08-04, Hopper). An operator whose auth
     user has NO clients row (admin_users only) can never mint an rr_ data token
     -- the session-token exchange 409s -- so useMe / useCustomers / useReports
     below stay token-gated and NEVER settle, leaving this component stuck on
     null (blank) and the tenant dashboard permanently empty. Route such an
     operator straight to /agency (the operator console runs off the Supabase
     JWT, not the rr_ token, so it loads cleanly).

     Signal source: getAdminMe() -- JWT-gated, so it resolves even with no rr_
     token. A NON-admin 403s here (query error) -> treated as "not admin" ->
     normal tenant flow, unchanged. An admin who ALSO owns a tenant returns
     has_tenant===true -> falls through to the normal dashboard default (and
     still sees the Operator-console link in the shell). We ONLY divert on an
     EXPLICIT has_tenant===false, so an older backend without the field (=>
     undefined) never wrongly bounces a tenant-owning admin. Shares the ['admin',
     'me'] cache key with AppShell / Agency, so no extra request. */
  const adminMe = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: ({ signal }) => api.getAdminMe(signal),
    retry: false,
    staleTime: 5 * 60_000,
  });

  /* ENTRY-MODEL-AWARE landing (2026-07-09 fix). The 2026-07-06 monetization gate
     used to bounce ANY no-subscription account straight to /choose-plan on login
     -- but that is the FRONT-DOOR-WALL behavior, and reverse_trial is the LOCKED
     model now: NO card/plan gate on login or dashboard entry; the card ask fires
     ONLY at the first real customer send (InlineCardStep / useRequireCardToSend).
     So we consult subscription access ONLY to hold during load, and we redirect
     to /choose-plan on `gated` ONLY in the front_door_wall arm. In reverse_trial
     a card-free owner lands in the product (brand-new -> /welcome chute; else ->
     /dashboard), matching RequireSubscription's own entry-model gating. This was
     the live bug: a returning card-free user was hard-sent to the trial/plan
     gate instead of the dashboard. */
  const entryModel = useEntryModel();
  const access = useSubscriptionAccess();

  /* ---- All hooks are declared above; from here it is pure branching. Rules of
     Hooks: no hook may follow a conditional return, so every early return lives
     below this line and the priority order is deliberate. ---- */

  /* 1. ADMIN-ONLY ACCOUNT (no tenant) -> operator console. HIGHEST priority:
     this account's rr_-token-gated queries (me/customers/reports) NEVER settle
     because the session-token exchange 409s, so we must decide off the
     JWT-gated admin check alone and must NOT fall through to the token-gated
     `settled` gate below (which would hang on null forever). Hold only until
     the fast admin check settles; a 403 counts as settled (= "not an admin",
     normal tenant flow). Diverts ONLY on an explicit has_tenant===false. */
  if (!(adminMe.isSuccess || adminMe.isError)) return null;
  if (adminMe.data?.is_admin === true && adminMe.data?.has_tenant === false) {
    return <Navigate to="/agency" replace />;
  }

  // 2. Front-door-wall arm only (off by default): hold during load, then gate
  //    a none/expired account to /choose-plan. reverse_trial never gates here.
  if (access.state === 'loading') return null;
  if (entryModel === 'front_door_wall' && access.state === 'gated') {
    return <Navigate to="/choose-plan" replace />;
  }

  // Hold routing until the three cheap signals have resolved (or errored). A
  // brief gate on first login only; the queries are token-gated so they fire
  // as soon as the session token lands.
  const settled =
    (me.isSuccess || me.isError) &&
    (customers.isSuccess || customers.isError) &&
    (reports.isSuccess || reports.isError);

  if (!settled) return null;

  // EVERY tenant lands on /dashboard (2026-08-05, Steve). Brand-new tenants used
  // to be force-routed to the /welcome onboarding CHUTE, which meant a trial
  // owner could never actually reach the product dashboard -- they were trapped
  // in the chute with no way to /dashboard. The dashboard is now fully viewable
  // at zero-data (intentional empty states) and carries the non-blocking
  // FinishSetupCard nudge, so setup is one tap away WITHOUT hiding the product.
  // The guided connect flow (/welcome/connect) is still reached on demand from
  // that nudge / the Integrations "Finish connecting Google" button. The
  // card/trial timing is untouched by this change.
  return <Navigate to="/dashboard" replace />;
}

export default PostAuthLanding;
